import './styles.scss';
import { Handle, Position } from '@xyflow/react';
import { IconMail, IconClock, IconTrash, IconArrowNarrowRight } from '@tabler/icons-react';
import { Text, Button } from '@fragments';
import StatusChip from '@fragments/StatusChip';
import { formatProjectedPct } from '@pages/CommunityConfig/tabs/Split/helpers/projectSplitAcceptance';

// Maps a split-invitation status → its badge + whether it can still be revoked.
// pending / awaiting_wallet are live (revocable); the rest are terminal records
// the owner can see but no longer act on.
const STATUS_CONFIG = {
    pending: { text: 'Pendente', variant: 'alert', revocable: true },
    awaiting_wallet: { text: 'Aguardando carteira', variant: 'alert', revocable: true },
    accepted: { text: 'Aceito', variant: 'success' },
    declined: { text: 'Recusado', variant: 'default' },
    expired: { text: 'Expirado', variant: 'default' },
    revoked: { text: 'Revogado', variant: 'default' },
};

function readEmail(invitation) {
    return invitation?.invited_email || invitation?.email || '';
}

function formatExpiration(iso) {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

export default function InvitationNode({ data }) {
    const { invitation, index, projectedPercentile, onRevokeInvitation } = data;

    const status = invitation?.status || 'pending';
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const email = readEmail(invitation);
    const percentile = invitation?.percentile;
    const expiration = formatExpiration(invitation?.expires_at);
    // The invite's percentile is already its absolute share of the total, so the
    // big number IS what it will receive. Only surface a projection when it would
    // actually differ — i.e. other open invites dilute it, or it's clamped to the
    // shrinking pool — otherwise it'd be a redundant restatement of the percentile.
    const hasProjection =
        config.revocable &&
        projectedPercentile != null &&
        Math.abs(Number(projectedPercentile) - Number(percentile || 0)) >= 0.01;

    return (
        <div className="split-invitation-node">
            <Handle
                type="target"
                position={Position.Left}
                className="split-touch-handle split-touch-handle--pending"
            />

            <div
                className="sin-card"
                style={{ animationDelay: `${index * 70}ms` }}
            >
                <StatusChip
                    text={config.text}
                    variant={config.variant}
                    className="sin-pill"
                />

                <div className="sin-icon">
                    <IconMail size={20} />
                </div>

                <div className="sin-identity">
                    <Text.h5 weight={500} contrast={8} className="sin-name">
                        {email || 'Convidado'}
                    </Text.h5>
                    {hasProjection ? (
                        <Text.span
                            contrast={6}
                            className="sin-sub sin-projection"
                            left={<IconArrowNarrowRight size={13} />}
                        >
                            será de {formatProjectedPct(projectedPercentile)}% do total
                        </Text.span>
                    ) : (
                        <Text.span
                            contrast={5}
                            className="sin-sub"
                            left={config.revocable && expiration ? <IconClock size={13} /> : undefined}
                        >
                            {config.revocable && expiration
                                ? `Expira em ${expiration}`
                                : 'Convite de split'}
                        </Text.span>
                    )}
                </div>

                <div className="sin-target">
                    <div className="sin-percent">
                        <Text.h2 contrast={8} className="sin-percent-value">
                            {percentile ?? 0}%
                        </Text.h2>
                        {config.revocable && onRevokeInvitation && (
                            <Button
                                className="sin-revoke"
                                discreet
                                color="error"
                                size="micro"
                                preventLoading
                                icon={<IconTrash />}
                                tooltip="Revogar convite"
                                onClick={() => onRevokeInvitation(invitation)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
